import { useEffect, useState } from 'react';
import { api, type Cupom, type Sessao } from '../api';
import { useRota } from '../rota';
import { diaCurto, emMoedas } from '../formato';
import { Carregando } from '../../../compartilhado/Espera';

const venceEm = (iso: string) => {
  const d = new Date(iso);
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  if (mesmoDia) return 'vence hoje';
  return `vence em ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const ROTULO_STATUS: Record<string, string> = {
  confirmado: 'usado',
  expirado: 'venceu',
  cancelado: 'cancelado',
};

/**
 * "usado em 08 set na loja 01 · 5 moedas" — o que der para dizer: a data
 * quando houver, a loja só quando houver, e o que ele custou (F4). Vale para o
 * balcão, para o caixa e para a troca feita no próprio caixa; a loja só
 * aparece quando a Unit mandar o `pdv_code` no apply (pedido 10 do contrato).
 *
 * Zero moeda é o cupom da CARTELA, cujo preço foram as visitas — dizer
 * "0 moedas" ali seria mentira, então o preço some.
 */
function usadoOnde(c: Cupom, nomeDaMoeda: string): string {
  if (c.status !== 'confirmado') return ROTULO_STATUS[c.status] ?? c.status;
  const quando = c.usadoEm ? `em ${diaCurto(c.usadoEm)}` : '';
  const onde = c.loja ? `na loja ${c.loja}` : '';
  const preco = c.moedas > 0 ? emMoedas(c.moedas, nomeDaMoeda) : '';
  return [['usado', quando, onde].filter(Boolean).join(' '), preco].filter(Boolean).join(' · ');
}

/** A lista de cupons: os ativos em destaque, o resto como história. */
export function Cupons({ sessao, aoFalhar }: { sessao: Sessao; aoFalhar: (e: unknown) => void }) {
  const { ir } = useRota();
  const [dados, setDados] = useState<{ ativos: Cupom[]; antigos: Cupom[] } | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api
      .cupons()
      .then(setDados)
      .catch((e) => {
        setErro('não consegui carregar seus cupons');
        aoFalhar(e);
      });
  }, [aoFalhar]);

  if (erro) return <div className="erro-tela">{erro}</div>;
  /* Primeira abertura: a lista ainda não existe, não há o que cobrir. */
  if (!dados)
    return (
      <div className="espera-tela">
        <Carregando />
      </div>
    );

  return (
    <div className="tela cp">
      <h1 className="titulo-tela">cupons</h1>

      {dados.ativos.length === 0 && dados.antigos.length === 0 && (
        <div className="cp-vazio">seus cupons de resgate aparecem aqui</div>
      )}

      {dados.ativos.map((c) => (
        <button key={c.codigo} className="cp-ativo" onClick={() => ir(`/cupom/${c.codigo}`)}>
          <div>
            <div className="cp-codigo">{`${c.codigo.slice(0, 3)} ${c.codigo.slice(3)}`}</div>
            <div className="cp-nome">{c.nome}</div>
          </div>
          <div className="cp-vence">{venceEm(c.expiraEm)}</div>
        </button>
      ))}

      {dados.antigos.length > 0 && (
        <>
          <div className="cp-secao">anteriores</div>
          {dados.antigos.map((c) => (
            <div key={c.codigo} className="cp-antigo">
              <span className="cp-antigo-nome">{c.nome}</span>
              <span className="cp-antigo-status">{usadoOnde(c, sessao.nomeDaMoeda)}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * A tela 5 do mockup: o cupom em TELA ESCURA, código gigante, para mostrar no
 * caixa. Com o cupom de 10 dias (decisão de 21/08), o relógio de contagem
 * regressiva do mockup virou a DATA de vencimento — mais honesto que minutos.
 */
export function CupomCheio({
  codigo,
  sessao,
  aoFalhar,
}: {
  codigo: string;
  sessao: Sessao;
  aoFalhar: (e: unknown) => void;
}) {
  const { ir } = useRota();
  const [cupom, setCupom] = useState<Cupom | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api
      .cupons()
      .then((d) => {
        const achado = [...d.ativos, ...d.antigos].find((c) => c.codigo === codigo) ?? null;
        if (!achado) setErro('cupom não encontrado');
        setCupom(achado);
      })
      .catch((e) => {
        setErro('não consegui carregar o cupom');
        aoFalhar(e);
      });
  }, [codigo, aoFalhar]);

  if (erro) return <div className="erro-tela">{erro}</div>;
  /* Escuro porque o cupom cheio é tela escura: a órbita inverte os tokens e a
     esfera não pisca um retângulo branco antes do código aparecer. */
  if (!cupom)
    return (
      <div className="espera-tela escuro">
        <Carregando />
      </div>
    );

  const ativo = cupom.status === 'reservado';

  return (
    <div className="cupom-cheio">
      <div className="cc-rotulo">{ativo ? 'cupom ativo' : ROTULO_STATUS[cupom.status] ?? cupom.status}</div>
      <div className="cc-codigo">{`${cupom.codigo.slice(0, 3)} ${cupom.codigo.slice(3)}`}</div>
      <div className="cc-nome">{cupom.nome}</div>
      <div className="cc-vence">
        {ativo
          ? venceEm(cupom.expiraEm)
          : cupom.status === 'confirmado'
            ? usadoOnde(cupom, sessao.nomeDaMoeda)
            : ''}
      </div>
      {ativo && <div className="cc-instrucao">peça ao atendente para validar antes de sair da loja</div>}
      <button className="botao-vazado escuro" onClick={() => ir('/cupons')}>
        Fechar
      </button>
    </div>
  );
}
